import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { CoreActions, CoreApiActions } from './core.action';
import { AppService } from '../services/app.service';
import { switchMap } from 'rxjs/operators';
import { Settings } from 'luxon';
import { CoreFacade } from '../core.facade';
import { TimezoneAppItemOutputDto } from '@shared/api/model/timezone-app-item-output-dto';

@Injectable()
export class CoreEffects {
    constructor(private readonly actions$: Actions,
        private appService: AppService,
        private coreFacade: CoreFacade,
    ) {
    }

    loadConfig$ = createEffect(() => this.actions$.pipe(
        ofType(CoreApiActions.loadAppConfig),
        switchMap(() => this.appService.getAppConfig().pipe(
            switchMap((data) =>
                [
                    CoreApiActions.loadAppConfigSuccess({ appConfig: data }),
                    CoreApiActions.loadTimezone({timezone: data.timezone || undefined})
                ]
            )
        ))
    ));

    loadTimezone$ = createEffect(() => this.actions$.pipe(
        ofType(CoreApiActions.loadTimezone),
        switchMap((defaultTimeZone) => this.appService.getAppTimezones().pipe(
            switchMap((data) => {
                const localStorageTimeZoneString = localStorage.getItem(CoreFacade.storageTimezoneKey);
                let localStorageTimeZone: TimezoneAppItemOutputDto | undefined;

                if (localStorageTimeZoneString) {
                    try {
                        localStorageTimeZone = JSON.parse(localStorageTimeZoneString) as TimezoneAppItemOutputDto;
                    } catch {
                        // Invalid stored value: ignore and fall back to API default.
                        localStorage.removeItem(CoreFacade.storageTimezoneKey);
                    }
                }

                // checks if timezone from local storage is intl supported time zone, else use timezone from app config api
                const timezone = localStorageTimeZone?.ianaId && this.coreFacade.isIanaIdIntlSupportedTimeZone(localStorageTimeZone.ianaId)
                    ? localStorageTimeZone
                    : data.items?.find(t => t.ianaId === defaultTimeZone.timezone);

                return [
                    CoreApiActions.loadTimezoneSuccess({timezonesList: data.items || []}),
                    CoreActions.setTimezone({ timezone })
                ];
            })
        ))
    ));

    setTimezone$ = createEffect(() => this.actions$.pipe(
        ofType(CoreActions.setTimezone),
        switchMap((timezone) => {

            Settings.defaultZone = timezone?.timezone?.ianaId ? timezone?.timezone?.ianaId : Settings.defaultZone;
            localStorage.setItem(CoreFacade.storageTimezoneKey, JSON.stringify(timezone?.timezone ? timezone.timezone : ""));

            return [CoreActions.setTimezoneSuccess(timezone)];
        })
    ));
}
